/**
 * The ten story beats of presenter mode, in the order of the signature
 * moments in PLAN.md section 4.2. A beat is a mode route plus a deep-link
 * state (network, selection, clock, speed, bottom tab, detail panel), then a
 * typed list of extra actions the controller runs in order, the presenter
 * docks to show while the shell chrome is hidden, a dwell time for the
 * auto-tour and the readiness checks that say when the beat counts as shown.
 *
 * Every beat sets the state its picture depends on, so the beats can be
 * jumped to in any order.
 */
import type { Mode, Selection } from '@/state/store';
import type { NetworkId } from '@/features/loader/types';
import type { OrbitPathMode, SceneMode } from '@/features/loader/globe/GlobeEngine';
import { GUAM } from '@/features/loader/constants';
import type { LoaderProfile } from '@/features/loader/store';
import type { LiveSource } from '@/features/twin/store';
import type { ViewMode } from '@/features/twin/types';
import { NOAA_ASSET_PREFIX } from '@/features/twin/data/antennaSpec';

/** Number keys in beat order: 1 to 9, then 0 for the tenth beat. */
export const BEAT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;
export type BeatKey = (typeof BEAT_KEYS)[number];

/** The deep-link part of a beat (the same fields as useDeepLink, applied through the stores). */
export interface BeatState {
  network?: NetworkId;
  /** Merged into useUI.selection; null clears a field, a missing field is left alone. */
  selection?: Partial<Selection>;
  /** 'live' follows wall time; 'now' starts simulated time at the current instant; else an ISO time. */
  time?: 'live' | 'now' | string;
  /** Clock multiplier for simulated time (not allowed with 'live'). */
  speed?: number;
  /** Bottom panel tab (useUI.setBottomTab, which also opens the panel). */
  tab?: string;
  /** Right detail panel. */
  panel?: 'open' | 'closed';
}

export type GuamStep = 'reset' | 'start' | 'addStation';

export type BeatAction =
  /** Navigate to another mode in the middle of the beat (the dive). */
  | { kind: 'mode'; mode: Mode }
  /** Hold before the next action (choreography, never a loading wait). */
  | { kind: 'wait'; ms: number }
  | { kind: 'sceneMode'; mode: SceneMode }
  | { kind: 'orbitPaths'; mode: OrbitPathMode }
  | { kind: 'coverage'; enabled: boolean }
  | { kind: 'contactLines'; enabled: boolean }
  /** City lights (Cesium ion asset). The terminator needs no action: CesiumGlobe always lights the globe. */
  | { kind: 'nightLights'; enabled: boolean }
  /** Camera follows a mission (useLoader.setTracked) once its entity is drawn; null stops following. */
  | { kind: 'follow'; missionId: string | null }
  /** Loader profile: the slim 'noaa-core' profile hides tabs a beat may dock (coverage gaps, budget). */
  | { kind: 'loaderProfile'; profile: LoaderProfile }
  /** Camera flight to an antenna of the loaded dataset. */
  | { kind: 'flyToAntenna'; antennaId: string }
  | { kind: 'runAnalysis' }
  /** The scripted Guam coverage-gap demo: reset clears it and removes the station, start draws the gap, addStation adds the 13 m station and re-runs. */
  | { kind: 'guam'; step: GuamStep }
  /** Twin view mode; explode (0 to 1) only with 'exploded'. */
  | { kind: 'twinView'; mode: ViewMode; explode?: number }
  | { kind: 'signalPath'; id: string | null }
  | { kind: 'subview'; id: string | null }
  /** Frame a named pose or one subsystem of the loaded asset. */
  | { kind: 'twinCamera'; poseId?: string; subsystemId?: string }
  | { kind: 'liveSource'; source: LiveSource }
  /** Start a procedure by timeline id, or stop the running one (null). */
  | { kind: 'procedure'; id: string | null }
  | { kind: 'failureScenarios'; ids: string[] }
  /** Open the GAIA column with this prompt waiting in its input. */
  | { kind: 'gaiaPrompt'; prompt: string }
  /** Glow the diorama nodes of a data-flow stage (null clears). */
  | { kind: 'overviewStage'; stageId: number | null };

export type BeatActionKind = BeatAction['kind'];

/** What must hold before a beat counts as shown. All checks must pass. */
export type ReadyCheck =
  | { kind: 'route'; mode: Mode }
  | { kind: 'globe' }
  | { kind: 'dataset'; network: NetworkId }
  /** Analysis results on screen (covering this antenna, when given), also while a re-run is in progress. */
  | { kind: 'analysis'; antennaId?: string }
  /** The store and the globe engine both follow this mission. */
  | { kind: 'tracking'; missionId: string }
  | { kind: 'guamDemo' }
  | { kind: 'guamStation' }
  /** Asset loaded in the twin store and the scene handle published. */
  | { kind: 'twin'; assetId: string }
  /** The live RFIMS feed for this site has delivered a payload. */
  | { kind: 'liveFeed'; assetId: string }
  | { kind: 'procedure'; id: string }
  | { kind: 'gaiaOpen' };

export interface Beat {
  id: string;
  key: BeatKey;
  title: string;
  /** One line on screen for the audience. */
  caption: string;
  /** For the presenter only: talking points, never shown on screen. */
  narration: string;
  /** Route the beat starts on; null stays where the presenter is. */
  mode: Mode | null;
  state: BeatState;
  actions: BeatAction[];
  /** Panels the presenter overlay docks on screen for this beat (the shell hides its own in presenter mode). */
  show?: { bottomPanel?: boolean; gaia?: boolean };
  /** Seconds the auto-tour stays on the beat once it is shown. */
  dwellSeconds: number;
  ready: ReadyCheck[];
}

/** The parametric twin of a NOAA antenna, through the twin's antenna-to-asset mapping (loadAssetSpec resolves `noaa:<id>`). */
export const FAIRBANKS_13M_ASSET = `${NOAA_ASSET_PREFIX}fairbanks_13m_a`;

/** Twin beats start from a clean scene: no procedure, no signal path, main view. */
const TWIN_RESET: BeatAction[] = [
  { kind: 'procedure', id: null },
  { kind: 'signalPath', id: null },
  { kind: 'subview', id: null },
];

export const BEATS: readonly Beat[] = [
  {
    id: 'opening-shot',
    key: '1',
    title: 'Opening shot',
    caption: 'The live NOAA constellation over a lit Earth, every contact drawn the moment it happens.',
    narration:
      'Everything here is live: positions propagate from current TLEs and each beam is a contact the visibility engine computed just now. The camera follows NOAA-20; the terminator and the city lights show day and night.',
    mode: 'operations',
    state: {
      network: 'noaa',
      time: 'live',
      selection: { missionId: 'noaa20', antennaId: null, subsystemId: null },
    },
    actions: [
      { kind: 'guam', step: 'reset' },
      { kind: 'sceneMode', mode: '3D' },
      { kind: 'nightLights', enabled: true },
      { kind: 'orbitPaths', mode: 'all' },
      { kind: 'coverage', enabled: true },
      { kind: 'contactLines', enabled: true },
      { kind: 'follow', missionId: 'noaa20' },
    ],
    dwellSeconds: 20,
    ready: [
      { kind: 'route', mode: 'operations' },
      { kind: 'globe' },
      { kind: 'dataset', network: 'noaa' },
      { kind: 'tracking', missionId: 'noaa20' },
    ],
  },
  {
    id: 'the-dive',
    key: '2',
    title: 'The dive',
    caption: 'From the whole network to one site: we fly to Wallops, then step inside the antenna twin.',
    narration:
      'The camera flies to the Wallops 26 m station, then the picture cuts to the hero RF antenna twin with the antenna framed. The cross-fade dive with the dish pointed at the satellite in contact is a later feature; beat 10 shows a correctly sized twin for any antenna.',
    mode: 'operations',
    state: {
      network: 'noaa',
      time: 'live',
      selection: { antennaId: 'wallops_26m', missionId: null, assetId: 'rf_antenna', subsystemId: null },
    },
    actions: [
      { kind: 'follow', missionId: null },
      { kind: 'sceneMode', mode: '3D' },
      { kind: 'flyToAntenna', antennaId: 'wallops_26m' },
      { kind: 'wait', ms: 800 },
      { kind: 'mode', mode: 'maintenance' },
      ...TWIN_RESET,
      { kind: 'twinView', mode: 'solid' },
      { kind: 'twinCamera', poseId: 'main' },
    ],
    dwellSeconds: 12,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: 'rf_antenna' },
    ],
  },
  {
    id: 'exploded-xray',
    key: '3',
    title: 'Exploded view and x-ray',
    caption:
      'The antenna opens into its subsystems, then the receive chain lights up from feed horn to storage.',
    narration:
      'Exploded view pulls the subsystems apart with labelled leader lines. X-ray ghosts the structure and traces the receive chain: feed horn, LNA, waveguide, frequency translator, IF distribution, IFL switch, modem, bit synchroniser, decoder, switch matrix, storage.',
    mode: 'maintenance',
    state: { time: 'live', selection: { assetId: 'rf_antenna', subsystemId: null } },
    actions: [
      ...TWIN_RESET,
      { kind: 'twinView', mode: 'exploded', explode: 1 },
      { kind: 'wait', ms: 5000 },
      { kind: 'twinView', mode: 'xray' },
      { kind: 'signalPath', id: 'receive' },
    ],
    dwellSeconds: 14,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: 'rf_antenna' },
    ],
  },
  {
    id: 'live-telemetry',
    key: '4',
    title: 'Live telemetry on metal',
    caption:
      'Real RFIMS site readings tint the container and monitoring unit, with thermal shading from the sensors.',
    narration:
      'RFIMS00 on the live feed: InfluxDB through the telemetry hub, within 30 s of the Telegraf write. If the upstream is unreachable the hub serves its stub and the source chip reads LIVE (demo data). Dish RF readings stay SIM until a real RF feed exists.',
    mode: 'maintenance',
    state: { time: 'live', selection: { assetId: 'rfims00', subsystemId: null } },
    actions: [
      ...TWIN_RESET,
      { kind: 'liveSource', source: 'live' },
      { kind: 'twinView', mode: 'thermal' },
      { kind: 'twinCamera', subsystemId: 'environmental' },
    ],
    dwellSeconds: 15,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: 'rfims00' },
      { kind: 'liveFeed', assetId: 'rfims00' },
    ],
  },
  {
    id: 'procedures',
    key: '5',
    title: 'Procedures as choreography',
    caption: 'The antenna maintenance procedure moves the camera and highlights each part, step by step.',
    narration:
      'Seven steps from the shipped Antenna Maintenance timeline; each step frames the parts it names and the strip shows the step. The full player with tool images and the caution banner is in the Procedures tab outside presenter mode.',
    mode: 'maintenance',
    state: { time: 'live', selection: { assetId: 'rf_antenna', subsystemId: null } },
    actions: [
      { kind: 'signalPath', id: null },
      { kind: 'subview', id: null },
      { kind: 'twinView', mode: 'solid' },
      { kind: 'procedure', id: 'TimelineAntennaMaintenance' },
    ],
    dwellSeconds: 42,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: 'rf_antenna' },
      { kind: 'procedure', id: 'TimelineAntennaMaintenance' },
    ],
  },
  {
    id: 'schedule-theatre',
    key: '6',
    title: 'Schedule theatre',
    caption:
      'The day replayed at 60 times speed: contacts fill the Gantt as their beams appear on the globe.',
    narration:
      'Simulated time from now at 60x. Each block in the Gantt is an allocated contact and the beams on the globe appear as the passes begin. The camera follows NOAA-20 through its passes.',
    mode: 'loader',
    state: {
      network: 'noaa',
      time: 'now',
      speed: 60,
      tab: 'gantt',
      selection: { missionId: 'noaa20', antennaId: null, subsystemId: null },
    },
    actions: [
      { kind: 'guam', step: 'reset' },
      { kind: 'sceneMode', mode: '3D' },
      { kind: 'orbitPaths', mode: 'selected' },
      { kind: 'coverage', enabled: true },
      { kind: 'contactLines', enabled: true },
      { kind: 'follow', missionId: 'noaa20' },
    ],
    show: { bottomPanel: true },
    dwellSeconds: 25,
    ready: [
      { kind: 'route', mode: 'loader' },
      { kind: 'globe' },
      { kind: 'dataset', network: 'noaa' },
      { kind: 'analysis' },
      { kind: 'tracking', missionId: 'noaa20' },
    ],
  },
  {
    id: 'click-to-place',
    key: '7',
    title: 'Click-to-place antenna',
    caption:
      'Add a 13 m station at Guam and the what-if engine re-runs the whole schedule with it in seconds.',
    narration:
      'The scripted coverage-gap demo: the Indian Ocean gap is drawn, the Guam 13 m station joins the network and the analysis re-runs. The blackout counter in the panel is the demo script (47 to 12 minutes); contacts and utilisation are computed.',
    mode: 'analysis',
    state: {
      network: 'noaa',
      time: 'live',
      tab: 'gaps',
      selection: { missionId: null, antennaId: null, subsystemId: null },
    },
    actions: [
      // The coverage-gaps tab (with the demo's counter) is hidden in the slim profile.
      { kind: 'loaderProfile', profile: 'full' },
      { kind: 'guam', step: 'reset' },
      { kind: 'follow', missionId: null },
      { kind: 'sceneMode', mode: '3D' },
      { kind: 'coverage', enabled: true },
      { kind: 'guam', step: 'start' },
      { kind: 'wait', ms: 4000 },
      { kind: 'guam', step: 'addStation' },
    ],
    show: { bottomPanel: true },
    dwellSeconds: 18,
    ready: [
      { kind: 'route', mode: 'analysis' },
      { kind: 'globe' },
      { kind: 'dataset', network: 'noaa' },
      { kind: 'guamDemo' },
      { kind: 'guamStation' },
      { kind: 'analysis', antennaId: GUAM.STATION_ID },
    ],
  },
  {
    id: 'presenter-mode',
    key: '8',
    title: 'Presenter mode',
    caption:
      'Every beat of this story is one key: 1 to 0 jump, T tours, X freezes the picture, D switches to demo data.',
    narration:
      'Presenter mode hides the chrome and runs the story from the keyboard. X holds the picture still mid-sentence; the corner badges show the clock, the data profile and whether this machine has the app cached for offline use.',
    mode: null,
    state: {},
    actions: [],
    dwellSeconds: 12,
    ready: [],
  },
  {
    id: 'gaia',
    key: '9',
    title: 'GAIA',
    caption: 'Ask GAIA about this antenna in plain language.',
    narration:
      'The GAIA column opens with a maintenance question waiting; press Enter in the column to send it. What GAIA reads and does depends on the GAIA 2.0 port.',
    mode: 'maintenance',
    state: { time: 'live', selection: { assetId: 'rf_antenna', subsystemId: null } },
    actions: [
      ...TWIN_RESET,
      { kind: 'twinView', mode: 'solid' },
      { kind: 'twinCamera', poseId: 'main' },
      {
        kind: 'gaiaPrompt',
        prompt: 'What is wrong with this antenna, and what should maintenance do first?',
      },
    ],
    show: { gaia: true },
    dwellSeconds: 20,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: 'rf_antenna' },
      { kind: 'gaiaOpen' },
    ],
  },
  {
    id: 'every-antenna',
    key: '0',
    title: 'Every antenna has a twin',
    caption:
      'Fairbanks 13 m A, generated from its network record: every antenna on the globe opens into a sized twin.',
    narration:
      'The parametric generator builds reflector, rig, pedestal and radome from the antenna record, so all 41 NOAA antennas have a twin; the artist-modelled antenna stays the hero.',
    mode: 'maintenance',
    state: {
      network: 'noaa',
      time: 'live',
      selection: {
        antennaId: 'fairbanks_13m_a',
        missionId: null,
        assetId: FAIRBANKS_13M_ASSET,
        subsystemId: null,
      },
    },
    actions: [...TWIN_RESET, { kind: 'twinView', mode: 'solid' }, { kind: 'twinCamera', poseId: 'main' }],
    dwellSeconds: 15,
    ready: [
      { kind: 'route', mode: 'maintenance' },
      { kind: 'twin', assetId: FAIRBANKS_13M_ASSET },
    ],
  },
];

/** Index of the beat bound to a number key, or -1. */
export function beatIndexForKey(key: string): number {
  return BEATS.findIndex((b) => b.key === key);
}

/** The number shown on screen for a beat (1 to 10). */
export function beatNumber(index: number): number {
  return index + 1;
}
